import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { authApi } from "@/lib/api/endpoints";
import { tokenStore } from "@/lib/api/tokens";
import type { Organization, User } from "@/lib/types/api";

interface AuthContextValue {
  user: User | null;
  organization: Organization | null;
  /** Undefined while the stored token is still being validated on boot. */
  ready: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setOrganization: (organization: Organization | null) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [organization, setOrganizationState] = useState<Organization | null>(null);
  const [ready, setReady] = useState(false);

  // A stored token may be expired or revoked, so it isn't trusted until /me
  // confirms it. Until then the app shows a boot state rather than flashing the
  // login screen at someone who is in fact signed in.
  useEffect(() => {
    let cancelled = false;

    async function restore() {
      if (!tokenStore.accessToken) {
        setReady(true);
        return;
      }
      try {
        const payload = await authApi.me();
        if (cancelled) return;
        setUser(payload.user);
        setOrganizationState(payload.organization);
      } catch {
        tokenStore.clear();
      } finally {
        if (!cancelled) setReady(true);
      }
    }

    void restore();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const session = await authApi.login(email, password);
    tokenStore.set(session.token, session.refresh_token);
    setUser(session.user);
    setOrganizationState(session.organization);
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Revocation is best-effort — the local session is cleared regardless.
    } finally {
      tokenStore.clear();
      setUser(null);
      setOrganizationState(null);
    }
  }, []);

  const setOrganization = useCallback((next: Organization | null) => {
    setOrganizationState(next);
    tokenStore.setOrganizationId(next?.id ?? null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, organization, ready, login, logout, setOrganization }),
    [user, organization, ready, login, logout, setOrganization],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside an AuthProvider");
  return context;
}
